/*
  Series strip on collection pages (sections/series-strip.liquid, #237).
  The tiles are series, brands, product types or part types (data-field: the field in the search
  index). Adds a photo per tile from the search server (Meilisearch): the photo of a product tagged
  "seriefoto" (series_photo in the index), else the best-selling product photo, else the photo of
  its exploded-drawing page. Products in this collection first, then the whole store (Sander on
  #237, 26 Oct 2026: a parts page showed tagged complete sets). Tile clicks go through the collection's
  own filtering (loadCollection in collection.js), which also replaces the strip.
*/

// "field:value" (normalised) -> image URL, or null when the search server has none.
// Kept across filter reloads, so a replaced strip gets its photos without a new request.
const seriesStripPhotos = new Map()

// Same as productTypeKey in the indexer: "Douche Slang" and "Doucheslang" are one value
const seriesStripKey = (field, name) => `${field}:${name.toLowerCase().replace(/[\s-]+/g, '')}`

const seriesStripQuote = (value) => `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`

class SeriesStrip extends HTMLElement {
  connectedCallback () {
    this.field = this.dataset.field || 'series'

    this.querySelector('[data-series-strip-toggle]')?.addEventListener('click', (event) => this.toggle(event.currentTarget))

    this.querySelectorAll('a[data-value], a[data-series-strip-clear]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (typeof loadCollection !== 'function') return
        event.preventDefault()
        window.history.replaceState({}, '', link.href)
        loadCollection()
      })
    })

    this.loadPhotos()
  }

  toggle (button) {
    const expanded = this.classList.toggle('is-expanded')
    button.setAttribute('aria-expanded', expanded)
    button.querySelector('[data-series-strip-toggle-text]').textContent = expanded ? button.dataset.textLess : button.dataset.textMore
  }

  async loadPhotos () {
    const tiles = [...this.querySelectorAll('[data-value]')]
    const missing = tiles.map((tile) => tile.dataset.value).filter((name) => !seriesStripPhotos.has(seriesStripKey(this.field, name)))

    if (missing.length && this.dataset.url && this.dataset.key) {
      await this.fetchPhotos(missing)
    }

    tiles.forEach((tile) => {
      const src = seriesStripPhotos.get(seriesStripKey(this.field, tile.dataset.value))
      const img = tile.querySelector('img')
      if (!src || !img) return
      img.addEventListener('load', () => img.closest('.series-strip-img').classList.add('has-img'), { once: true })
      img.src = src
      img.hidden = false
    })
  }

  async fetchPhotos (names) {
    const field = this.field
    // Only photos of the collection's own brands
    let vendors = []
    try {
      vendors = JSON.parse(this.dataset.vendors || '[]')
    } catch (error) {}
    const vendor = vendors.length ? `vendor IN [${vendors.map(seriesStripQuote).join(', ')}] AND ` : ''
    const filter = `${vendor}${field} IN [${names.map(seriesStripQuote).join(', ')}] AND has_image = true`
    const query = (extra) => ({
      indexUid: 'products',
      q: '',
      filter: `${filter} AND ${extra}`,
      distinct: field,
      limit: names.length,
      attributesToRetrieve: [field, 'image']
    })
    const steps = ['series_photo = true', 'parts_page = false', 'parts_page = true']
    // Collection ids are numeric; on /collections/all or vendors there's no match and the store-wide steps answer.
    const collection = /^\d+$/.test(this.dataset.collection || '') ? this.dataset.collection : ''
    const inCollection = collection ? [`collections = '${collection}' AND `] : []

    try {
      const response = await fetch(`${this.dataset.url}/multi-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${this.dataset.key}` },
        // In this order: products the store tagged "seriefoto", then the best-selling product photo,
        // then the drawing page. Within each, the index ranks bestsellers first.
        body: JSON.stringify({ queries: [...inCollection, ''].flatMap((scope) => steps.map((step) => query(scope + step))) }),
        signal: window.AbortSignal?.timeout ? AbortSignal.timeout(4000) : undefined
      })
      if (!response.ok) return
      const { results } = await response.json()

      const wanted = new Set(names.map((name) => seriesStripKey(field, name)))
      const found = new Map()
      // The first query with a photo for a value wins. vendor is a single value, the others lists.
      results.forEach((result) => {
        result.hits.forEach((hit) => {
          [].concat(hit[field] || []).forEach((name) => {
            const key = seriesStripKey(field, name)
            if (!wanted.has(key) || found.has(key)) return
            const url = new URL(hit.image)
            url.searchParams.set('width', '320')
            found.set(key, url.toString())
          })
        })
      })
      wanted.forEach((key) => seriesStripPhotos.set(key, found.get(key) || null))
    } catch (error) {
      // No photos: the tiles still work with name and count
    }
  }
}

if (!customElements.get('series-strip')) {
  customElements.define('series-strip', SeriesStrip)
}
